import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PaginaLoader } from "@/components/shared/PaginaLoader";
import { KennisOpstelhulp } from "@/components/admin/KennisOpstelhulp";
import { adminKnowledgeApi, vertaalApiFout } from "@/lib/api";
import { knowledgeKeys, useKennisItem } from "@/lib/hooks/useKnowledge";
import type {
  KennisItemAanmaken,
  KennisItemAntwoord,
  KennisItemSchrijfAntwoord,
  KennisItemWijzigen,
  KennisOpstelhulpVoorstel,
  KennisVerificatieStatus,
} from "@/types/api";

type FormState = {
  vraag: string;
  antwoord: string;
  categorie: string;
  automatiseringsniveau: 0 | 2;
  verificatie_status: KennisVerificatieStatus;
  actief: boolean;
  wijziging_reden: string;
};

const LEEG: FormState = {
  vraag: "",
  antwoord: "",
  categorie: "",
  automatiseringsniveau: 0,
  verificatie_status: "ongecontroleerd",
  actief: true,
  wijziging_reden: "",
};

const VERIFICATIE_STATUSSEN: KennisVerificatieStatus[] = [
  "ongecontroleerd",
  "actueel",
  "verouderd",
];

function vanRespons(item: KennisItemAntwoord): FormState {
  return {
    vraag: item.vraag,
    antwoord: item.antwoord,
    categorie: item.categorie,
    automatiseringsniveau: item.automatiseringsniveau === 2 ? 2 : 0,
    verificatie_status: item.verificatie_status,
    actief: item.actief,
    wijziging_reden: "",
  };
}

/** Toon een waarschuwing als de embedding niet is vernieuwd, zodat de
 *  beheerder weet dat het item niet doorzoekbaar is voor Sanne. */
function meldEmbedding(
  res: KennisItemSchrijfAntwoord,
  t: (k: string, o?: Record<string, unknown>) => string,
) {
  if (!res.embedding_ok) {
    toast.warning(
      t("admin.knowledge.embedding_waarschuwing", {
        fout: res.embedding_fout ?? t("admin.knowledge.embedding_onbekend"),
      }),
      { duration: 8000 },
    );
  }
}

export function KnowledgeFormPagina() {
  const { t } = useTranslation();
  const { kennisId } = useParams<{ kennisId: string }>();
  const isNieuw = !kennisId || kennisId === "nieuw";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: bestaand, isLoading } = useKennisItem(
    isNieuw ? undefined : kennisId,
  );

  const [state, setState] = useState<FormState>(LEEG);
  const [fout, setFout] = useState<string | null>(null);
  // Laatst toegepaste AI-voorstel — voor de aandachtspunten (open_vragen)
  // boven het formulier.
  const [voorstel, setVoorstel] = useState<KennisOpstelhulpVoorstel | null>(
    null,
  );

  // State synchroniseren met server-data zodra geladen.
  useEffect(() => {
    if (bestaand) setState(vanRespons(bestaand));
  }, [bestaand]);

  /** Vul het formulier met een AI-voorstel. Reeds ingevulde inhoudsvelden
   *  worden niet stilzwijgend overschreven: vraag eerst een bevestiging. */
  const pasVoorstelToe = (v: KennisOpstelhulpVoorstel) => {
    const heeftInhoud =
      state.vraag.trim().length > 0 ||
      state.antwoord.trim().length > 0 ||
      state.categorie.trim().length > 0;
    if (
      heeftInhoud &&
      !window.confirm(t("admin.knowledge.ai.bevestig_overschrijven"))
    ) {
      return;
    }
    setState((s) => ({
      ...s,
      vraag: v.vraag,
      antwoord: v.antwoord,
      categorie: v.categorie,
      automatiseringsniveau: v.automatiseringsniveau === 2 ? 2 : 0,
    }));
    setVoorstel(v);
    setFout(null);
  };

  const aanmaken = useMutation({
    mutationFn: () => {
      const payload: KennisItemAanmaken = {
        vraag: state.vraag.trim(),
        antwoord: state.antwoord.trim(),
        categorie: state.categorie.trim(),
        automatiseringsniveau: state.automatiseringsniveau,
        wijziging_reden: state.wijziging_reden.trim() || null,
      };
      return adminKnowledgeApi.aanmaken(payload);
    },
    onSuccess: (res) => {
      toast.success(t("admin.knowledge.aangemaakt"));
      meldEmbedding(res, t);
      void qc.invalidateQueries({ queryKey: knowledgeKeys.all });
      navigate("/kennis");
    },
    onError: (f) => setFout(vertaalApiFout(f)),
  });

  const wijzigen = useMutation({
    mutationFn: () => {
      const payload: KennisItemWijzigen = {
        vraag: state.vraag.trim(),
        antwoord: state.antwoord.trim(),
        categorie: state.categorie.trim(),
        automatiseringsniveau: state.automatiseringsniveau,
        verificatie_status: state.verificatie_status,
        actief: state.actief,
        wijziging_reden: state.wijziging_reden.trim() || null,
      };
      return adminKnowledgeApi.wijzigen(kennisId!, payload);
    },
    onSuccess: (res) => {
      toast.success(t("admin.knowledge.opgeslagen"));
      meldEmbedding(res, t);
      void qc.invalidateQueries({ queryKey: knowledgeKeys.all });
      void qc.invalidateQueries({
        queryKey: knowledgeKeys.detail(kennisId!),
      });
      navigate("/kennis");
    },
    onError: (f) => setFout(vertaalApiFout(f)),
  });

  if (!isNieuw && isLoading) return <PaginaLoader />;

  const valideer = (): string | null => {
    if (state.vraag.trim().length === 0)
      return t("admin.knowledge.fout.vraag_verplicht");
    if (state.antwoord.trim().length === 0)
      return t("admin.knowledge.fout.antwoord_verplicht");
    if (state.categorie.trim().length === 0)
      return t("admin.knowledge.fout.categorie_verplicht");
    return null;
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = valideer();
    if (v) {
      setFout(v);
      return;
    }
    setFout(null);
    if (isNieuw) aanmaken.mutate();
    else wijzigen.mutate();
  };

  const bezig = aanmaken.isPending || wijzigen.isPending;
  const titel = isNieuw
    ? t("admin.knowledge.titel_nieuw")
    : t("admin.knowledge.titel_bewerken");

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/kennis">
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t("admin.knowledge.terug")}
          </Link>
        </Button>
      </div>

      <h1 className="text-[26px] font-bold tracking-tight">{titel}</h1>

      <KennisOpstelhulp onVoorstel={pasVoorstelToe} voorstel={voorstel} />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("admin.knowledge.gegevens")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="vraag">{t("admin.knowledge.veld.vraag")} *</Label>
              <Textarea
                id="vraag"
                value={state.vraag}
                onChange={(e) =>
                  setState((s) => ({ ...s, vraag: e.target.value }))
                }
                required
                rows={2}
                placeholder={t("admin.knowledge.veld.vraag_hint")}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="antwoord">
                {t("admin.knowledge.veld.antwoord")} *
              </Label>
              <Textarea
                id="antwoord"
                value={state.antwoord}
                onChange={(e) =>
                  setState((s) => ({ ...s, antwoord: e.target.value }))
                }
                required
                rows={8}
                placeholder={t("admin.knowledge.veld.antwoord_hint")}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="categorie">
                  {t("admin.knowledge.veld.categorie")} *
                </Label>
                <Input
                  id="categorie"
                  value={state.categorie}
                  onChange={(e) =>
                    setState((s) => ({ ...s, categorie: e.target.value }))
                  }
                  required
                  maxLength={128}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="automatiseringsniveau">
                  {t("admin.knowledge.veld.automatiseringsniveau")}
                </Label>
                <select
                  id="automatiseringsniveau"
                  value={state.automatiseringsniveau}
                  onChange={(e) =>
                    setState((s) => ({
                      ...s,
                      automatiseringsniveau: Number(e.target.value) === 2 ? 2 : 0,
                    }))
                  }
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value={0}>
                    {t("admin.knowledge.niveau.0")}
                  </option>
                  <option value={2}>
                    {t("admin.knowledge.niveau.2")}
                  </option>
                </select>
              </div>
            </div>

            {!isNieuw && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="verificatie_status">
                    {t("admin.knowledge.veld.verificatie_status")}
                  </Label>
                  <select
                    id="verificatie_status"
                    value={state.verificatie_status}
                    onChange={(e) =>
                      setState((s) => ({
                        ...s,
                        verificatie_status: e.target
                          .value as KennisVerificatieStatus,
                      }))
                    }
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {VERIFICATIE_STATUSSEN.map((v) => (
                      <option key={v} value={v}>
                        {t(`admin.knowledge.status.${v}`)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end pb-1.5">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={state.actief}
                      onChange={(e) =>
                        setState((s) => ({ ...s, actief: e.target.checked }))
                      }
                    />
                    {t("admin.knowledge.veld.actief")}
                  </label>
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="wijziging_reden">
                {t("admin.knowledge.veld.wijziging_reden")}
              </Label>
              <Input
                id="wijziging_reden"
                value={state.wijziging_reden}
                onChange={(e) =>
                  setState((s) => ({ ...s, wijziging_reden: e.target.value }))
                }
                placeholder={t("admin.knowledge.veld.wijziging_reden_hint")}
              />
            </div>

            {fout && (
              <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
                {fout}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button asChild variant="ghost" type="button">
                <Link to="/kennis">
                  {t("admin.knowledge.annuleren")}
                </Link>
              </Button>
              <Button type="submit" disabled={bezig}>
                {bezig && (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                )}
                {isNieuw
                  ? t("admin.knowledge.aanmaken")
                  : t("admin.knowledge.opslaan")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
